import { useTranslation } from "react-i18next";
import { visiblePages } from "./Pagination";

/** "Sve": every row on one page (Strahinja, 2026-10-05). */
export const ALL_ROWS = "all";
export const PAGE_SIZES = [10, 20, 50, 100, ALL_ROWS] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

/**
 * The most rows one request returns for a server-paged list (Spring's page size cap and the
 * advanced search's own). With "Sve" a longer list is still split, into pages of this size,
 * instead of silently showing only its beginning.
 */
export const SERVER_ALL_LIMIT = 2000;

/** The `size` to ask the server for. */
export function requestPageSize(pageSize: PageSize): number {
  return pageSize === ALL_ROWS ? SERVER_ALL_LIMIT : pageSize;
}

/** Remembered page size for one table (`localStorage` key), else the default. */
export function readPageSize(storageKey: string, fallback: PageSize = 20): PageSize {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === ALL_ROWS) return ALL_ROWS;
    const n = Number(raw);
    return (PAGE_SIZES as readonly (number | string)[]).includes(n) ? (n as PageSize) : fallback;
  } catch {
    return fallback;
  }
}

export function storePageSize(storageKey: string, size: PageSize) {
  try { window.localStorage.setItem(storageKey, String(size)); } catch { /* private mode etc. */ }
}

/**
 * Classic table footer for server-side paging (Strahinja, 2026-09-10): "shown X–Y of N",
 * previous / numbered pages / next, and rows per page 10 · 20 · 50 · 100 · Sve. Always rendered
 * (even on one page) so the controls do not jump in and out.
 */
export function TableFooter({ page, pageSize, total, onPage, onPageSize, allLimit }: {
  page: number;
  pageSize: PageSize;
  total: number;
  onPage: (next: number) => void;
  onPageSize: (next: PageSize) => void;
  /** Rows per page behind "Sve" when the list is paged by the server; leave out when every row is already loaded. */
  allLimit?: number;
}) {
  const { t } = useTranslation();
  const size = pageSize === ALL_ROWS ? Math.max(1, allLimit ?? total) : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const from = total === 0 ? 0 : page * size + 1;
  const to = Math.min(total, (page + 1) * size);
  const pages = visiblePages(page, totalPages);

  return (
    <div className="table-footer">
      <span className="table-footer-range muted">
        {total === 0
          ? t("table.empty", "Nema redova")
          : t("table.range", "Prikazano {{from}}–{{to}} od {{total}}", { from, to, total })}
      </span>
      <nav className="table-footer-pages" aria-label={t("table.pagination", "Stranice")}>
        <button type="button" className="btn sm" onClick={() => onPage(page - 1)} disabled={page === 0}>
          {t("pagination.previous", "← Prethodna")}
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`ell-${i}`} className="muted">…</span>
          ) : (
            <button
              key={p}
              type="button"
              className={`btn sm${p === page ? " primary" : ""}`}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onPage(p)}
            >
              {p + 1}
            </button>
          ),
        )}
        <button type="button" className="btn sm" onClick={() => onPage(page + 1)} disabled={page >= totalPages - 1}>
          {t("pagination.next", "Sledeća →")}
        </button>
      </nav>
      <div className="table-footer-size">
        <span className="muted">{t("table.rowsPerPage", "Redova po strani")}</span>
        <div className="chip-group" role="radiogroup" aria-label={t("table.rowsPerPage", "Redova po strani")}>
          {PAGE_SIZES.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={s === pageSize}
              className={`chip${s === pageSize ? " active" : ""}`}
              onClick={() => onPageSize(s)}
              title={s === ALL_ROWS && allLimit !== undefined
                ? t("table.allLimited", "Svi redovi na jednoj strani; lista duža od {{limit}} redova se deli na strane", { limit: allLimit })
                : undefined}
            >
              {s === ALL_ROWS ? t("table.all", "Sve") : s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
